import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import MobileLayout, { SwitchToDesktopLink } from '../MobileLayout';
import { apiGet } from '../api';
import { useBackgroundReports } from '../../contexts/BackgroundReportContext';

const PRIMARY = '#1993e5';

interface ReportListItem {
  id: string;
  title?: string;
  name?: string;
  workflowType?: string;
  reportType?: string;
  customerId?: string;
  customer?: { id: string; name: string };
  updatedAt?: string;
  createdAt?: string;
}

const fmtDate = (iso?: string) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  const day = 86400000;
  if (diff < day) return 'today';
  if (diff < 2 * day) return 'yesterday';
  if (diff < 30 * day) return `${Math.floor(diff / day)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const humanWorkflow = (t?: string) => (t || '').replace(/_/g, ' ');

const MobileReports: React.FC = () => {
  const { pendingReports, completedReports } = useBackgroundReports();
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // When a background report finishes, refresh the saved-reports list so the
  // newly-saved item appears in the bottom section without a hard refresh.
  const completedSignal = completedReports
    .filter((r) => r.savedReportId)
    .map((r) => r.workflowId)
    .join(',');

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    apiGet<any[]>('/comparison-reports')
      .then((data) => {
        if (cancel) return;
        setReports((data || []).map((r) => ({
          id: r.id,
          title: r.title,
          name: r.name,
          workflowType: r.workflowType,
          reportType: r.reportType,
          customerId: r.customerId,
          customer: r.customer,
          updatedAt: r.updatedAt,
          createdAt: r.createdAt,
        })));
      })
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load reports'))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [completedSignal]);

  const filtered = reports.filter((r) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (
      (r.title || r.name || '').toLowerCase().includes(q) ||
      (r.customer?.name || '').toLowerCase().includes(q) ||
      (r.workflowType || r.reportType || '').toLowerCase().includes(q)
    );
  });

  return (
    <MobileLayout title="Reports" topRight={<SwitchToDesktopLink />}>
      <div className="sticky top-0 z-10 bg-slate-50 px-3 pb-2 pt-3 dark:bg-slate-950">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search reports…"
          className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        />
      </div>

      {pendingReports.length > 0 && (
        <section className="mx-3 mt-3 overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
          <header
            className="flex items-center gap-2 border-b border-slate-100 px-3 py-2 dark:border-slate-800"
            style={{ color: PRIMARY }}
          >
            <span className="inline-block h-2 w-2 animate-pulse rounded-full" style={{ backgroundColor: PRIMARY }} />
            <span className="text-xs font-semibold uppercase tracking-wide">
              {pendingReports.length} {pendingReports.length === 1 ? 'report' : 'reports'} running
            </span>
          </header>
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {pendingReports.map((p) => (
              <li key={p.workflowId} className="px-3 py-3">
                <div className="text-sm font-semibold text-slate-900 dark:text-white">
                  {humanWorkflow(p.workflowType)}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {p.customerName}
                  {p.documents && p.documents.length > 0 ? ` · ${p.documents.length} doc${p.documents.length === 1 ? '' : 's'}` : ''}
                </div>
                <div className="text-[11px] text-slate-400">Started {fmtDate(p.startedAt)}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {loading && <div className="px-3 py-6 text-center text-sm text-slate-500">Loading…</div>}
      {error && <div className="mx-3 my-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">{error}</div>}

      <ul className="mt-3 divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
        {filtered.map((r) => (
          <li key={r.id}>
            <Link to={`/m/reports/${r.id}`} className="flex items-center justify-between gap-3 px-3 py-3 active:bg-slate-50 dark:active:bg-slate-800">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-slate-900 dark:text-white">
                  {r.title || r.name || 'Untitled report'}
                </div>
                <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                  {r.customer?.name || ''}{r.workflowType ? ` · ${humanWorkflow(r.workflowType)}` : ''}
                </div>
                <div className="text-[11px] text-slate-400">{fmtDate(r.updatedAt || r.createdAt)}</div>
              </div>
              <span className="text-slate-300">›</span>
            </Link>
          </li>
        ))}
        {!loading && filtered.length === 0 && pendingReports.length === 0 && (
          <li className="px-3 py-8 text-center text-sm text-slate-500">No reports yet.</li>
        )}
      </ul>
    </MobileLayout>
  );
};

export default MobileReports;
